// plate/printed.mjs — what the PDF reads off a Plate value (Stage 8). Pure data, imported by app.js (the Typst walker,
// the import's section map) and by plate/mount.jsx (which diagrams get a picture). It replaced fund/v16's
// plate/adapter.mjs (a Plate value back to TipTap JSON, for the walker over TipTap JSON; both removed in Stage 8), and
// keeps its rules exactly: the PDF printed from these is the PDF fund/v16 printed, byte for byte
// (test/golden-v16.test.mjs).
//
// ⛔ Each rule below moves bytes in the PDF:
//   - Leaves merge into one run when their marks are equal as JSON, attributes that never print included (a mark's
//     basis; a link's target, rel and class). The walker places a question's #super after the run that completes
//     its anchor, and prints a honesty sign once per run, so a merge moves both.
//   - Marks are in one fixed order, link, bold, code, italic, strike, sugg, fundSug, never the leaf's key order: the
//     walker wraps innermost-out and `code` replaces what came before it.
//   - A "\n" in a leaf is a break: no anchor text, and the run on either side never merges across it.
//   - Lists are rebuilt from the flat lines: a line with listStyleType and indent > 0 opens a list; a line at the
//     same indent opens an item, except that listFirst, a listRestart or a change of style starts a new list; a
//     deeper line opens a list inside the last item; anything else at that depth or deeper joins the last item —
//     a top-level diagram there included, which is then neither printed nor numbered.
const BULLETS = new Set(["disc", "circle", "square"]);
const orNull = (v) => (v === undefined || v === "" ? null : v);

function leafMarks(leaf, link) {
  const marks = [];
  if (link) marks.push({ type: "link", attrs: { href: link.url, target: orNull(link.target), rel: orNull(link.rel), class: orNull(link.className) } });
  if (leaf.bold) marks.push({ type: "bold" });
  if (leaf.code) marks.push({ type: "code" });
  if (leaf.italic) marks.push({ type: "italic" });
  if (leaf.strikethrough) marks.push({ type: "strike" });
  for (const t of ["sugg", "fundSug"]) if (leaf[t]) marks.push({ type: t, attrs: { kind: leaf[t].kind, basis: orNull(leaf[t].basis) } });
  return marks;
}

export function printedRuns(children) {
  const out = [];
  const push = (leaf, link) => {
    const marks = leafMarks(leaf, link), key = JSON.stringify(marks);
    String(leaf.text || "").split("\n").forEach((part, k) => {
      if (k > 0) out.push({ br: true });
      if (!part) return;
      const last = out[out.length - 1];
      if (last && !last.br && last.key === key) last.text += part;
      else out.push({ text: part, marks, key });
    });
  };
  for (const c of children || []) {
    if (c.type === "a") for (const l of c.children || []) push(l, c);
    else if ("text" in c) push(c, null);
  }
  return out;
}

export function plateNodeText(n) { return "text" in n ? n.text || "" : (n.children || []).map(plateNodeText).join(""); }

class PrintedList { constructor(ordered, items) { this.ordered = ordered; this.items = items; } }
// By shape, not instanceof: this module is bundled twice (plate.bundle.js through mount.jsx, app.bundle.js through
// app.js), each copy with its own class. A Plate node carries `children`, never `items`.
export const isPrintedList = (e) => Array.isArray(e.items) && "ordered" in e;

function list(nodes, i) {
  const first = nodes[i], indent = first.indent, style = first.listStyleType, items = [];
  for (; i < nodes.length; i++) {
    const n = nodes[i], depth = n.indent || 0;
    if (depth < indent) break;
    if (depth === indent && n.listStyleType) {
      if (items.length && (n.listFirst || n.listRestart != null || n.listStyleType !== style)) break;
      items.push({ fundSug: orNull((n.listItemMark || {}).fundSug), blocks: [n] });
    } else if (depth > indent && n.listStyleType) {
      const [sub, next] = list(nodes, i);
      items[items.length - 1].blocks.push(sub);
      i = next - 1;
    } else items[items.length - 1].blocks.push(n);
  }
  return [new PrintedList(!BULLETS.has(style), items), i];
}

export function printedBlocks(nodes) {
  const out = [];
  for (let i = 0; i < (nodes || []).length;) {
    if (nodes[i].listStyleType && nodes[i].indent > 0) { const [l, next] = list(nodes, i); out.push(l); i = next; }
    else out.push(nodes[i++]);
  }
  return out;
}

// For each pictured diagram, in PDF order, its index among the value's top-level diagrams. Counted by position, never
// by identity: a list takes its run of lines, diagrams included, and a value may hold one diagram object twice.
export const isDiagram = (n) => n.type === "viz" || n.type === "systems_map";
const diagramsIn = (l) => l.items.reduce((s, it) => s + it.blocks.reduce((t, b) => t + (isPrintedList(b) ? diagramsIn(b) : isDiagram(b) ? 1 : 0), 0), 0);
export function printedDiagrams(value) {
  const out = [];
  let k = 0;
  for (const e of printedBlocks(value || [])) {
    if (isPrintedList(e)) k += diagramsIn(e);
    else if (isDiagram(e)) out.push(k++);
  }
  return out;
}
